import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Combobox,
  Divider,
  Group,
  InputBase,
  Loader,
  Modal,
  MultiSelect,
  Stack,
  Text,
  TextInput,
  useCombobox,
} from '@mantine/core';
import {
  ADMIN_PROGRAM_LABELS,
  type CHURCH_ADMIN_PROGRAM,
  type ChurchAdmin,
  type ChurchAdminSearchItem,
} from '@/types/users';
import { useDebounceValue } from 'usehooks-ts';
import styles from './OrganizationAdminModal.module.sass';
import { SaveErrorBox } from '@/components/SaveErrorBox/SaveErrorBox';
import type { AxiosError } from 'axios';
import type { ErrorResponse } from '@/types/error';

type UserLookupResult = ChurchAdminSearchItem & { exists: boolean };

export type OrganizationAdminFormValues = {
  email: string;
  firstName: string;
  lastName: string;
  programs: CHURCH_ADMIN_PROGRAM[];
  userId: string | null;
  ssoUserId: string | null;
};

type OrganizationAdminModalProps = {
  opened: boolean;
  isEditMode: boolean;
  initialAdmin?: ChurchAdmin | null;
  enabledPrograms: CHURCH_ADMIN_PROGRAM[];
  onClose: () => void;
  onSave: (args: OrganizationAdminFormValues) => Promise<void> | void;
  onRemove: () => Promise<void> | void;
  onEmailLookup?: (email: string) => Promise<ChurchAdminSearchItem[]>;
};

export const OrganizationAdminModal = ({
  opened,
  isEditMode,
  initialAdmin,
  onClose,
  onSave,
  onRemove,
  onEmailLookup,
  enabledPrograms,
}: OrganizationAdminModalProps) => {
  const [email, setEmail] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [selectedPrograms, setSelectedPrograms] = useState<CHURCH_ADMIN_PROGRAM[]>([]);
  const [isCheckingEmail, setIsCheckingEmail] = useState<boolean>(false);
  const [emailLookup, setEmailLookup] = useState<UserLookupResult | null>(null);
  const [emailOptions, setEmailOptions] = useState<ChurchAdminSearchItem[]>([]);
  const [emailSearch, setEmailSearch] = useState<string>('');
  const [debouncedEmailSearch] = useDebounceValue(emailSearch, 500);
  const emailCombobox = useCombobox({ onDropdownClose: () => emailCombobox.resetSelectedOption() });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<AxiosError<ErrorResponse> | null>(null);

  const resetForm = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setSelectedPrograms([]);
    setEmailLookup(null);
    setIsCheckingEmail(false);
    setEmailOptions([]);
    setEmailSearch('');
    setSaveError(null);
  };

  useEffect(() => {
    if (!opened) {
      resetForm();
      return;
    }
    if (isEditMode && initialAdmin) {
      setEmail(initialAdmin.email);
      setFirstName(initialAdmin.firstName || '');
      setLastName(initialAdmin.lastName || '');
      setSelectedPrograms(initialAdmin.programs || []);
      setEmailLookup({
        userId: initialAdmin.userId,
        email: initialAdmin.email,
        exists: true,
        firstName: initialAdmin.firstName,
        lastName: initialAdmin.lastName,
        ssoUserId: initialAdmin.ssoUserId,
      });
      return;
    }
    if (!isEditMode) {
      resetForm();
    }
  }, [opened, isEditMode, initialAdmin]);

  const programOptions = useMemo(
    () => enabledPrograms.map((p) => ({ value: p, label: ADMIN_PROGRAM_LABELS[p] })),
    [enabledPrograms]
  );

  const isEmailValid = (value: string) => /[^@\s]+@[^@\s]+\.[^@\s]+/.test(value);

  const canSave = useMemo(() => {
    const hasEmail = isEmailValid(email);
    const hasNames = firstName.trim().length > 0 && lastName.trim().length > 0;
    const hasPrograms = selectedPrograms.length > 0;
    const isLookupResolved = isEditMode || Boolean(emailLookup);
    return hasEmail && hasNames && hasPrograms && isLookupResolved && !isCheckingEmail && !isSaving;
  }, [email, firstName, lastName, selectedPrograms, isCheckingEmail, isSaving, isEditMode, emailLookup]);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailSearch(value);
    setEmailLookup(null);
    if (!value) {
      setEmailOptions([]);
      return;
    }
    emailCombobox.openDropdown();
  };

  useEffect(() => {
    const searchEmail = async () => {
      if (!onEmailLookup) return;
      if (!debouncedEmailSearch) return;
      setIsCheckingEmail(true);
      try {
        const results = await onEmailLookup(debouncedEmailSearch);
        setEmailOptions(results);
        if (results.length === 0) {
          setEmailLookup({
            email: debouncedEmailSearch,
            exists: false,
            userId: null,
            ssoUserId: null,
            firstName: '',
            lastName: '',
          });
        }
      } finally {
        setIsCheckingEmail(false);
      }
    };
    if (debouncedEmailSearch && debouncedEmailSearch.length >= 3) {
      searchEmail();
    }
  }, [debouncedEmailSearch, onEmailLookup]);

  const getEmailOptionValue = (option: ChurchAdminSearchItem) =>
    `${option.userId ?? 'no-user-id'}:${option.ssoUserId ?? 'no-sso-id'}:${option.email}`;

  const handleEmailOptionSelect = (option: ChurchAdminSearchItem) => {
    setEmail(option.email);
    setFirstName(option.firstName || '');
    setLastName(option.lastName || '');
    setEmailLookup({ ...option, exists: true });
    setEmailOptions([]);
    emailCombobox.closeDropdown();
  };

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave({
        email,
        firstName,
        lastName,
        programs: selectedPrograms,
        userId: isEditMode ? (initialAdmin?.userId ?? null) : (emailLookup?.userId ?? null),
        ssoUserId: isEditMode ? (initialAdmin?.ssoUserId ?? null) : (emailLookup?.ssoUserId ?? null),
      });
    } catch (error: unknown) {
      console.error('Failed to save admin:', error);
      setSaveError(error as AxiosError<ErrorResponse> | null);
    } finally {
      setIsSaving(false);
    }
  };

  const title = isEditMode ? 'Edit Admin' : 'Add Admin';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size="lg"
      classNames={{
        header: styles.modalHeader,
        title: styles.modalTitle,
        close: styles.closeButton,
      }}
    >
      <Stack gap="md" pt="sm">
        <Stack gap={4}>
          <Combobox
            store={emailCombobox}
            withinPortal={false}
            onOptionSubmit={(val) => {
              const found = emailOptions.find((o) => getEmailOptionValue(o) === val);
              if (found) handleEmailOptionSelect(found);
            }}
          >
            <Combobox.Target>
              <InputBase
                component="input"
                label="Email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => handleEmailChange(e.currentTarget.value)}
                disabled={Boolean(isEditMode && initialAdmin)}
                error={email.length > 0 && !isEmailValid(email) ? 'Enter a valid email' : undefined}
                rightSection={isCheckingEmail ? <Loader size="xs" /> : undefined}
                required
              />
            </Combobox.Target>
            <Combobox.Dropdown>
              <Combobox.Options>
                {emailOptions.length === 0 ? (
                  <Combobox.Empty>No users found</Combobox.Empty>
                ) : (
                  emailOptions.map((item) => (
                    <Combobox.Option key={getEmailOptionValue(item)} value={getEmailOptionValue(item)}>
                      <Stack gap={0}>
                        <Text>{item.email}</Text>
                        <Text c="dimmed" size="xs">
                          {item.firstName || ''} {item.lastName || ''}
                        </Text>
                      </Stack>
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </Combobox.Dropdown>
          </Combobox>
          {!isEditMode && (
            <Group gap="xs" align="center">
              {isCheckingEmail ? (
                <Loader size="xs" />
              ) : emailLookup ? (
                emailLookup.exists ? (
                  <Text c="dimmed">
                    User found: {emailLookup.firstName} {emailLookup.lastName}
                  </Text>
                ) : (
                  <Text c="dimmed">No user found. A new user will be created.</Text>
                )
              ) : null}
            </Group>
          )}
          {isEditMode && emailLookup && (
            <Group gap="xs" align="center">
              <Text c="dimmed">
                Editing existing user: {emailLookup.firstName} {emailLookup.lastName}
              </Text>
            </Group>
          )}
        </Stack>

        <Stack gap={4}>
          <Group grow>
            <TextInput
              label="Name"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.currentTarget.value)}
              required
            />
            <TextInput
              label=" "
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.currentTarget.value)}
            />
          </Group>
        </Stack>

        <Stack gap={4}>
          <MultiSelect
            label="Role"
            data={programOptions}
            value={selectedPrograms}
            onChange={(values) => setSelectedPrograms(values as CHURCH_ADMIN_PROGRAM[])}
            placeholder="Select programs"
            searchable={false}
            nothingFoundMessage="No programs"
            clearable={false}
            error={selectedPrograms.length === 0 ? 'Select at least one program' : undefined}
            required
            mb="xl"
          />
        </Stack>
        <Divider my="xs" />
        <SaveErrorBox error={saveError} />
        <Group justify="space-between">
          {isEditMode ? (
            <Button color="red" variant="light" onClick={onRemove}>
              Remove Admin
            </Button>
          ) : (
            <span />
          )}
          <Group>
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!canSave} loading={isSaving}>
              Save
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};
